import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
} from "@tanstack/react-table";
import { useLocation } from "react-router";

import { Paginator } from "./paginator/paginator";
import {
  COLORS,
  STYLES,
  FILTER_LABEL,
  DEFAULT_VALUE,
  PAGINATION_INDEXING,
  PAGINATION_PAGE_SIZES,
  TABLE_COLUMN_DEFAULTS,
  emptyString,
  columnsWithDarkGrayBackground,
  columnsWithLightGrayBackground,
  viewPathConstants,
} from "../../../constants";
import { commonUnitTestsIdsConstants } from "../../../constants/unitTestConstants";
import { getClassName, onKeyDownHandler } from "../../utility";

export const DataTable = ({
  columns,
  data,
  numberOfRowsPerPage,
  totalRows,
  pageNumber,
  pageSize,
  onRowClick = () => { },
  onPageChange = () => { },
  onColumnSort = () => { },
  sortedBy,
  onSizeChange = () => { },
  addBorder,
  noPagination,
  totalPositionCenter,
  isRowHighlight,
  isPaginationDropDownEnable,
  isPaginationAfterFiveRecords,
  customClassForTableContainer = "px-4",
  customClassForTable = emptyString,
  customClassForRows = emptyString,
  paginationCustomClass = emptyString,
}) => {
  const location = useLocation();
  const currentPageRef = useRef(pageNumber || PAGINATION_INDEXING.FIRST_PAGE_NUMBER);
  const currentPageSizeRef = useRef(pageSize);
  const [activePage, setActivePage] = useState(pageNumber || PAGINATION_INDEXING.FIRST_PAGE_NUMBER);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  // eslint-disable-next-line
  const [sorting, setSorting] = useState([sortedBy]);
  const [changeBackground, setChangeBackground] = useState(emptyString);

  const manualPageSize = useMemo(
    () => [
      {
        value: PAGINATION_PAGE_SIZES.DEFAULT,
        label: PAGINATION_PAGE_SIZES.DEFAULT,
      },
      { value: PAGINATION_PAGE_SIZES.LARGE, label: PAGINATION_PAGE_SIZES.LARGE },
      { value: totalRows, label: FILTER_LABEL.ALL },
    ],
    [totalRows]
  );

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: TABLE_COLUMN_DEFAULTS.RESIZE_MODE,
    state: {
      sorting,
    },
    tableOptions: { defaultColumn: TABLE_COLUMN_DEFAULTS.WIDTHS },
    enableResizing: true,
    sortDescFirst: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Request each selected page once using the server's page index.
  useEffect(() => {
    if (onPageChange && currentPageRef.current !== activePage) {
      currentPageRef.current = activePage;
      onPageChange(activePage - PAGINATION_INDEXING.PAGE_INDEX_OFFSET);
    }
  }, [activePage, onPageChange]);

  //Use effect to change page size
  useEffect(() => {
    if (
      onSizeChange &&
      currentPageSize &&
      currentPageSizeRef.current !== currentPageSize
    ) {
      currentPageSizeRef.current = currentPageSize;
      onSizeChange(currentPageSize, PAGINATION_INDEXING.FIRST_PAGE_INDEX);
    }
  }, [currentPageSize, onSizeChange]);

  //Use effect to apply sorting when column clicked
  useEffect(() => {
    if (sorting.length !== 0) {
      if (
        sorting &&
        (sorting[0].id !== sortedBy.id || sorting[0].desc !== sortedBy.desc)
      ) {
        onColumnSort(sorting[0], activePage - PAGINATION_INDEXING.PAGE_INDEX_OFFSET);
      }
    }
    // eslint-disable-next-line
  }, [sorting]);

  // Follow page resets from filters without requesting the page again.
  useEffect(() => {
    const nextPage = pageNumber || PAGINATION_INDEXING.FIRST_PAGE_NUMBER;
    currentPageRef.current = nextPage;
    setActivePage(nextPage);
  }, [pageNumber]);
  // Keep All selected when filtering changes the result count.
  useEffect(() => {
    const currentSize = Number(currentPageSize);
    if (
      currentSize > PAGINATION_PAGE_SIZES.LARGE &&
      totalRows > PAGINATION_PAGE_SIZES.LARGE &&
      totalRows !== currentSize
    ) {
      setCurrentPageSize(totalRows);
    }
  }, [currentPageSize, totalRows]);

  const count = totalRows || DEFAULT_VALUE;
  const rowsPerPage = numberOfRowsPerPage || currentPageSize;
  const totalPages = Math.ceil(count / rowsPerPage);
  const getColumnBackgroundColor = (id) => {
    if (columnsWithDarkGrayBackground.includes(id)) {
      return COLORS.DARK_GREY;
    } else if (
      (columnsWithLightGrayBackground.includes(id) &&
        location.pathname === viewPathConstants.SUPER_ADMIN_MARKETING_SALES) ||
      location.pathname === viewPathConstants.BRAND_ADMIN_MARKETING_SALES
    ) {
      return COLORS.LIGHT_GREY;
    }
    return COLORS.WHITE;
  };

  const getColumnBorderStyle = (id) => {
    if (id === columnsWithDarkGrayBackground[3]) {
      return STYLES.DASHED;
    }
    return STYLES.NONE;
  };

  return (
    <div className={customClassForTableContainer}>
      <div
        className={`table-container w-100 overflow-auto ${getClassName(
          addBorder,
          "bordered-table"
        )}`}
      >
        <table
          className={`table table-hover min-width-table ${customClassForTable}`}
        >
          <thead className="table-head font-12-px">
            {React.Children.toArray(
              table.getHeaderGroups().map((headerGroup, _index) => (
                <tr
                  key={headerGroup.id}
                  data-testid={commonUnitTestsIdsConstants.TABLE_HEADER}
                >
                  {React.Children.toArray(
                    headerGroup.headers.map((column) => (
                      //Add the sorting props to control sorting. For this example
                      //we can add them into the header props
                      <th
                        key={column.id}
                        className="pos-relative table-data bold-6 align-content-center"
                        {...{
                          colSpan: column.colSpan,
                          style: {
                            width: column.getSize(),
                            backgroundColor: getColumnBackgroundColor(
                              column.id
                            ),
                            borderRight: getColumnBorderStyle(column.id),
                          },
                        }}
                      >
                        {!column.isPlaceholder && (
                          <>
                            <div
                              role="button"
                              tabIndex={
                                column.column.getCanSort() === false
                                  ? "-1"
                                  : "0"
                              }
                              className={getClassName(
                                column.column.getCanSort(),
                                "cursor-pointer select-none"
                              )}
                              onClick={column.column.getToggleSortingHandler()}
                              onKeyDown={(e) => {
                                onKeyDownHandler(e, () => {
                                  column.column.toggleSorting();
                                });
                              }}
                            >
                              {flexRender(
                                column.column.columnDef.header,
                                column.getContext()
                              )}
                              {sorting.length &&
                                column.getContext().column.id === sortedBy.id ? (
                                <span>
                                  <i
                                    className={`${sortedBy.desc
                                      ? "fa fa-sort-desc"
                                      : "fa fa-sort-asc"
                                      } ps-1`}
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </div>
                            <div
                              {...{
                                onMouseDown: column.getResizeHandler(),
                                onTouchStart: column.getResizeHandler(),
                                className: `resizer-v8 ${getClassName(
                                  column.column.getIsResizing(),
                                  "is-resizing"
                                )}`,
                              }}
                            />
                          </>
                        )}
                      </th>
                    ))
                  )}
                </tr>
              ))
            )}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              React.Children.toArray(
                table.getRowModel().rows.map((row) => {
                  return (
                    <tr
                      key={row.id}
                      data-testid={commonUnitTestsIdsConstants.TABLE_ROWS}
                      onClick={() => {
                        if (onRowClick) {
                          onRowClick(row.original);
                          setChangeBackground(row.id);
                        }
                      }}
                      className={getClassName(
                        changeBackground === row.id && isRowHighlight,
                        "table-active"
                      )}
                    >
                      {React.Children.toArray(
                        row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={`table-data bold-4 py-3 ${customClassForRows}`}
                            {...{
                              style: {
                                width: cell.column.getSize(),
                                borderRight: getColumnBorderStyle(
                                  cell.column.id
                                ),
                              },
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))
                      )}
                    </tr>
                  );
                })
              )
            ) : (
              <tr data-testid={commonUnitTestsIdsConstants.TABLE_ROWS}>
                <td colSpan="14" className="text-center">
                  No data to display
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!noPagination && (
        <Paginator
          paginationCustomClass={paginationCustomClass}
          totalRows={count}
          totalPositionCenter={totalPositionCenter}
          totalPages={totalPages}
          activePageNumber={activePage}
          activatePage={setActivePage}
          rowsPerPage={data?.length}
          currentPageSize={currentPageSize}
          activateCurrentPageSize={setCurrentPageSize}
          size={rowsPerPage}
          isPaginationDropDownEnable={isPaginationDropDownEnable}
          isPaginationAfterFiveRecords={isPaginationAfterFiveRecords}
          manualPageSize={manualPageSize}
        />
      )}
    </div>
  );
};
